import React, { useState } from 'react';
import styled from 'styled-components';
import { EwalletInfo } from '../../../interfaces/ewallet-info';
import TopBackNavBar from '../../../components/BackNavBar';
import { RedButton } from '../../../components/Button';
import EwalletCard from '../EwalletCard';
import { TransactionInfoTax } from '../../../interfaces/transaction-info-tax';
import { addCommas, formatCurrencyNumber } from '../../../utils/amountFormat';
import TextInput from '../../../components/TextInput';
import useMakeWalletRefund from '../../../hooks/useMakeWalletRefund';
import BodyText from '../../../components/BodyText';
import { Pages } from '../../../App';
import BigNumber from 'bignumber.js';

interface Props {
	setTransactionId: any;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	selectedEwallet: EwalletInfo;
	transaction: TransactionInfoTax;
	setDefaultConfirmationEmail: any;
	setRefundAmount: React.Dispatch<React.SetStateAction<BigNumber>>;
}

const EwalletRefundReviewContent = ({ setTransactionId, setCurrentPage, selectedEwallet, transaction, setDefaultConfirmationEmail, setRefundAmount }: Props) => {
	const [refundType, setRefundType] = useState<string>('');
	const [partialAmt, setPartialAmt] = useState<string>('');
	const partialAmtVal = BigNumber(partialAmt);
	const [displayedAmt, setDisplayedAmt] = useState<string>('$ ');
	// Following used when processing a request
	const [isButtonProcessing, setIsButtonProcessing] = useState(false);

	const [errorState, setErrorState] = useState<boolean>(false);
	const errorMessage = 'Invalid. Please enter item value(s) only.';

	const [makeWalletRefund] = useMakeWalletRefund();

	const handleChange = (value: any) => {
		const numericValue = value.replace(/[^\d.]/g, '');
		const match = numericValue.match(/^\d*\.?\d{0,2}$/);
		if (match) {
			setPartialAmt(numericValue);
			setDisplayedAmt(`$ ${addCommas(numericValue)}`);
		}
	};

	const handleRefundSubmit = () => {
		const payload = {
			amount: transaction.refundableAmountLessTax.toNumber(),
			note: 'Ewallet Refund',
		};
		if (refundType === 'PARTIAL') {
			if (partialAmtVal.gt(0) && partialAmtVal.lte(transaction.refundableAmountLessTax)) {
				setErrorState(false);
				payload.amount = partialAmtVal.toNumber();
				window._satellite.track('choose-refund-submit', { type: 'partial' });
				submitRefund(payload);
			} else {
				setErrorState(true);
			}
		} else {
			window._satellite.track('choose-refund-submit', { type: 'full' });
			submitRefund(payload);
		}
	};

	const submitRefund = async (payload: any) => {
		setIsButtonProcessing(true);
		const { data, error } = await makeWalletRefund(selectedEwallet.id, transaction.id, payload);
		if (error) {
			console.error(error);
			setIsButtonProcessing(false);
			if (error.status === 401) {
				setCurrentPage(Pages.Landing);
			} else {
				setCurrentPage(Pages.Error);
			}
		} else if (data != null) {
			setDefaultConfirmationEmail(selectedEwallet.ownersEmail);
			setRefundAmount(BigNumber(data.amount));
			setTransactionId(data.id);
			setCurrentPage(Pages.Confirmation);
		}
	};

	return (
		<>
			<TopBackNavBar setCurrentPage={setCurrentPage} title="Choose Refund Amount" location={Pages.eWalletRefundTransactions} />
			<EwalletPage>
				<PageWrapper>
					<EwalletDiv>
						<EwalletCard item={selectedEwallet} />
					</EwalletDiv>
					<TransactionDiv>
						<StyledHeading>Choose Refund Amount</StyledHeading>
						<EwalletWrapper>
							<ResponsiveContainer>
								<RefundOption>
									<Label>
										<RadioButton
											type="radio"
											name="radioChoice"
											value="FULL"
											onClick={() => {
												setRefundType('FULL');
												setErrorState(false);
												setPartialAmt('');
											}}
										/>
										<RefundInfo>
											<SaleInfo>Full Refund</SaleInfo>
											<AmountInfo>{formatCurrencyNumber(transaction.refundableAmount.toNumber())}</AmountInfo>
										</RefundInfo>
									</Label>
									<HorizontalLineBreak />
								</RefundOption>
								<RefundOption>
									<Label>
										<RadioButton type="radio" name="radioChoice" value="PARTIAL" onClick={() => setRefundType('PARTIAL')} />
										<RefundInfo>
											<SaleInfo>Partial Refund</SaleInfo>
										</RefundInfo>
									</Label>
									<AmountDiv>
										<TextInput disabled={refundType === 'FULL' || !refundType} label="&nbsp;" value={displayedAmt} onChange={handleChange} error={errorState} errorText={errorMessage} />
									</AmountDiv>
								</RefundOption>
							</ResponsiveContainer>
						</EwalletWrapper>
					</TransactionDiv>
				</PageWrapper>
				<TaxText>Please note: If sales tax was collected, the refund will automatically include the tax amount on the next screen.</TaxText>
				<StyledRedButton disabled={!refundType || isButtonProcessing} handleClick={handleRefundSubmit}>
					Submit Refund
				</StyledRedButton>
			</EwalletPage>
		</>
	);
};

const EwalletPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const PageWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	margin: auto;
	gap: 45px;
	margin-top: 45px;
	margin-bottom: 20px;

	@media only screen and (max-width: 760px) {
		margin-top: 24px;
		gap: 30px;
		flex-direction: column;
	}
`;

const EwalletDiv = styled.div`
	width: 280px;
`;

const TransactionDiv = styled.div`
	width: 360px;
	text-align: left;
	height: 100%;

	@media only screen and (max-width: 760px) {
		width: 280px;
	}
`;

const TaxText = styled(BodyText)`
	width: 280px;
	margin: 0 0 23px 0;
`;

const StyledRedButton = styled(RedButton)`
	width: 280px;
	margin-bottom: 45px;
`;

const StyledHeading = styled.h1`
	font-size: 24px;
	font-weight: 400;
	line-height: 28px;
	color: #333;
	margin: 0;
`;

const HorizontalLineBreak = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 18px 0;
`;

const EwalletWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin: 25px 0;
`;

const ResponsiveContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	margin: auto;
	flex-direction: column;
`;

const RefundOption = styled.div`
	width: 100%;
`;

const Label = styled.label`
	display: flex;
	flex-direction: row;
	width: 100%;
`;

const RadioButton = styled.input`
	width: 20px;
	height: 20px;
	margin-top: 1px;
`;

const RefundInfo = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding-left: 5px;
`;

const AmountInfo = styled.p`
	font-size: 20px;
	font-weight: 400;
	line-height: 23px;
	margin: 0;
`;

const SaleInfo = styled.p`
	font-size: 13px;
	font-weight: 600;
	line-height: 15px;
	margin: 0;
`;

const AmountDiv = styled.div`
	width: 100%;
`;

export default EwalletRefundReviewContent;
